/**
 * pbxConstants.js
 *
 * Single source of truth for the hardcoded values used by the PBX softphone
 * (dialer, control center, agent state) — agent statuses, status colors,
 * UI <-> backend state mapping and the break reason list.
 */

// ============================ Agent statuses ============================
export const AGENT_STATUS = {
	AVAILABLE: 'available',
	BREAK: 'break',
	OFFLINE: 'offline'
};

export const AGENT_STATUS_LIST = [
	{ key: AGENT_STATUS.AVAILABLE, label: 'Available' },
	{ key: AGENT_STATUS.BREAK, label: 'Break' },
	{ key: AGENT_STATUS.OFFLINE, label: 'Offline' }
];

// Reported by the presence list while an agent is mid-call — not a status an
// agent can pick for themselves via handleAgentStatusChange.
export const AGENT_STATUS_ON_CALL = 'on_call';

export const AGENT_STATUS_COLORS = {
	[AGENT_STATUS.AVAILABLE]: '#34c759',
	[AGENT_STATUS.BREAK]: '#ff9500',
	[AGENT_STATUS.OFFLINE]: '#8e8e93',
	[AGENT_STATUS_ON_CALL]: '#ff3b30'
};

// ============================ Call transfer ============================
export const TRANSFER_POLL_INTERVAL_MS = 8000;
export const TRANSFER_PANEL_TITLE = 'Transfer Call';

// ============================ Supervisor monitor / coach / conference ============================
// Silent-monitor / whisper-coach / barge-conference into another agent's live
// call. Gated behind SUPERVISE_ACL_KEY plus the portal-admin + PBX-admin
// checks in PBXCallContext — see canUseSupervise.
export const SUPERVISE_MODES = {
	MONITOR: 'monitor',
	COACHING: 'coaching',
	CONFERENCE: 'conference'
};

export const SUPERVISE_MODE_OPTIONS = [
	{ key: SUPERVISE_MODES.MONITOR, label: 'Monitor' },
	{ key: SUPERVISE_MODES.COACHING, label: 'Coaching' },
	{ key: SUPERVISE_MODES.CONFERENCE, label: 'Conference' }
];

// Dialed as `${prefix}${agentExtension}` — e.g. Monitor on ext. 4555 dials 554555.
export const SUPERVISE_FEATURE_CODES = {
	[SUPERVISE_MODES.MONITOR]: '55',
	[SUPERVISE_MODES.COACHING]: '56',
	[SUPERVISE_MODES.CONFERENCE]: '57'
};

export const SUPERVISE_PANEL_TITLES = {
	[SUPERVISE_MODES.MONITOR]: 'Monitor Agent',
	[SUPERVISE_MODES.COACHING]: 'Coach Agent',
	[SUPERVISE_MODES.CONFERENCE]: 'Conference In'
};

// Must be created under Settings -> System Permissions and assigned to the
// admin's role before this ACL check can pass — see canUseSupervise.
export const SUPERVISE_ACL_KEY = 'FRONT:/apps/callcenter/dialer/supervise';

// SIP connection colors (control center icons + status bar).
export const SIP_STATUS_COLORS = {
	connected: '#34c759',
	disconnected: '#ff3b30',
	break: '#ff9500'
};

// ============================ Break reasons ============================
/**
 * Reasons an agent can pick when switching to the "Break" status.
 * The picked `value` is sent as `reason` to the /pbx/agent/state API.
 *
 * ADD / EDIT THE REASONS HERE — the dialog renders whatever is in this array.
 */
export const PBX_BREAK_REASONS = [
	{ value: 'lunch', label: 'Lunch Break' },
	{ value: 'tea', label: 'Tea Break' },
	{ value: 'prayer', label: 'Prayer Break' },
	{ value: 'meeting', label: 'Meeting' },
	{ value: 'training', label: 'Training' },
	{ value: 'personal', label: 'Personal' }
];

// Copy for the break reason dialog.
export const BREAK_REASON_DIALOG_TITLE = 'Break Reason';
export const BREAK_REASON_DIALOG_SUBTITLE = 'Select a reason for going on break';
